import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GetNews } from 'app/api/news';
import { showWarning } from 'app/store/root-slice';

import { RootState } from 'app/store/store';
import { INews } from './types';

interface GetNewsResponse {
  results: INews[];
  total?: number;
}

export interface InitialState {
  list: INews[];
  statuses: {
    onLoading: boolean; // в процессе загрузки
    loaded: boolean; // Загружено
    allLoaded: boolean; // Больше нет новостей
    lastRequestCount: number;
  };
  filters: {
    limit: number;
    offset: number;
    category?: string;
    resource?: string;
  };
}

export const initialState: InitialState = {
  list: [],
  statuses: {
    onLoading: false,
    loaded: false,
    allLoaded: false,
    lastRequestCount: 0,
  },
  filters: {
    limit: 10,
    offset: 0,
  },
};

export const getNews = createAsyncThunk<GetNewsResponse>(
  'news/getNews',
  async (_, { rejectWithValue, dispatch, getState }) => {
    const { news } = getState() as RootState;
    try {
      const response = await GetNews({
        limit: news.filters.limit,
        offset: news.filters.offset,
        ...(news.filters.category ? { category: news.filters.category } : {}),
        ...(news.filters.resource ? { resource: news.filters.resource } : {}),
      });
      return {
        results: response.results || [],
        total: response.total,
      };
    } catch (e) {
      dispatch(showWarning());
      return rejectWithValue(e);
    }
  }
);

export const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    setNewsFilters: (
      state,
      action: PayloadAction<{
        category?: string;
        resource?: string;
      }>
    ) => {
      const { category, resource } = action.payload;
      state.filters.offset = 0;
      state.filters.limit = 10;
      state.filters.category = category;
      state.filters.resource = resource;
      state.list = [];
      state.statuses.allLoaded = false;
      state.statuses.lastRequestCount = 0;
      state.statuses.loaded = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getNews.pending, (state) => {
      state.statuses.onLoading = true;
    });

    builder.addCase(getNews.rejected, (state) => {
      state.statuses.onLoading = false;
    });

    builder.addCase(
      getNews.fulfilled,
      (state, action: PayloadAction<GetNewsResponse>) => {
        const requestResults = action.payload.results || [];
        state.list = [...state.list, ...requestResults];
        state.filters.offset += state.filters.limit;
        state.statuses.allLoaded =
          typeof action.payload.total === 'number'
            ? state.list.length >= action.payload.total
            : requestResults.length === 0;
        state.statuses.onLoading = false;
        state.statuses.loaded = true;
        state.statuses.lastRequestCount = requestResults.length;
      }
    );
  },
});

export const { setNewsFilters } = newsSlice.actions;

export default newsSlice.reducer;
